import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PERSON_CHILD_KEYS,
  PERSON_ERROR_CODES,
  PERSON_PHOTO_KEYS,
  PERSON_SCALAR_KEYS,
  type ConnectionTypeInUseErrorBody,
  type ConnectionTypeNameTakenErrorBody,
  type PersonArchiveOnlyErrorBody,
  type PersonDeleteResponse,
  type PersonDetailResponse,
  type PersonDetailWire,
  type PersonIndexRowWire,
} from '@releve/shared';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { ApiError, authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';
import type { CreatePersonData, UpdatePersonData } from '@/types/people';

/**
 * People data hooks — every read and write goes through the Hono `people`
 * router (`apps/api/src/routes/people/people.ts`, `/api/v1/people`). Nothing here
 * touches `people` / `person_*` directly: those tables are backend-write-only
 * (`20260924072532_people_backend_write_lockdown.sql`), and a RESTRICTIVE
 * block on UPDATE/DELETE fails SILENTLY (0 rows), so a direct write would look
 * like a success.
 *
 * Query keys, all under the bare `['people']` prefix:
 * - index  `['people', orgId, 'index', {}]` (via `indexQueryOptions`)
 * - detail `['people', orgId, 'detail', code]`
 */
export const personKeys = {
  all: ['people'] as const,
  org: (organizationId: string | undefined) => ['people', organizationId] as const,
  detail: (organizationId: string | undefined, code: string | undefined) =>
    ['people', organizationId, 'detail', code] as const,
};

const peopleBase = (organizationId: string) => `/api/v1/people/orgs/${organizationId}`;

/**
 * Picker order: last name, then first name (what the old `order('last_name')`
 * gave every person dropdown). Module-level so `select` keeps a stable
 * identity and TanStack only re-runs it when the data changes.
 */
export function sortPeopleByName(rows: PersonIndexRowWire[]): PersonIndexRowWire[] {
  return [...rows].sort(
    (a, b) =>
      a.last_name.localeCompare(b.last_name, undefined, { sensitivity: 'base' }) ||
      a.first_name.localeCompare(b.first_name, undefined, { sensitivity: 'base' }) ||
      a.id.localeCompare(b.id),
  );
}

/** True when `row.person_type` shares at least one entry with `types` (an empty/absent filter matches all). */
export function personTypeOverlaps(row: Pick<PersonIndexRowWire, 'person_type'>, types?: readonly string[]): boolean {
  if (!types || types.length === 0) return true;
  return (row.person_type ?? []).some((type) => types.includes(type));
}

/** Case-insensitive haystack match over the view's `search_text` (already lowercased). */
export function personMatchesSearch(row: Pick<PersonIndexRowWire, 'search_text'>, search?: string): boolean {
  const needle = search?.trim().toLowerCase();
  if (!needle) return true;
  return (row.search_text ?? '').includes(needle);
}

/**
 * The COMPLETE org people index (`person_index` view), for the people grid,
 * the map and every person picker. Search, filters and sorting run
 * client-side.
 *
 * Liveness — every writer that changes a `person_index` column invalidates the
 * bare `['people']` (or `['people', orgId]`) prefix:
 * - `useCreatePerson` / `useUpdatePerson` / `useDeletePerson` (this file)
 * - `useUpsertPersonDEI` / `useDeletePersonDEI` (`usePersonDEI.ts`)
 * - `useUpsertPersonBusiness` (`usePersonBusiness.ts` — creates a business and
 *   a `business_contacts` row → `business_ids` / `organization_names`)
 * - `useImageUpload` / `useImageDelete` for `entityType: 'person'`
 *   (`profile_photo_small`)
 * - `BusinessContactsTab` add / update / remove contact (`business_*`,
 *   `person_type`)
 * - `useUpdateBusiness` / `useDeleteBusiness` (a rename or delete changes
 *   `business_names` / `organization_names`)
 * - `useUpdateSkill` / `useDeleteSkill`, `useUpdateDiscipline` /
 *   `useDeleteDiscipline` (`skill_names` / `discipline_names`)
 * - `useCreateInteraction` / `useUpdateInteraction` / `useDeleteInteraction` /
 *   `useToggleInteractionFollowUp` (`useInteractions.ts` — `last_contact_on`,
 *   `next_follow_up_on`, `interaction_count`, `interaction_project_*`,
 *   `interaction_type_*`, `open_follow_up`; create / update also link a
 *   participant to a business → `business_ids` / `organization_names`)
 * The interactions reminder job only stamps `follow_up_notified_at`, which no
 * `person_index` column reads.
 */
export function usePersonIndex(
  organizationId: string | undefined,
  options?: { sortByName?: boolean },
) {
  return useQuery({
    ...indexQueryOptions<PersonIndexRowWire>({
      entity: 'people',
      organizationId,
      path: `/api/v1/people/orgs/${organizationId}/index`,
    }),
    select: options?.sortByName ? sortPeopleByName : undefined,
  });
}

/**
 * The full person (`PersonDetailWire`) for the detail page and the edit form.
 * Keeps the old `(code, orgId)` argument order. A 404 resolves to `null`, so
 * `PersonDetails` keeps its not-found branch.
 */
export function usePersonByCode(code?: string, organizationId?: string) {
  return useQuery({
    queryKey: personKeys.detail(organizationId, code),
    queryFn: async (): Promise<PersonDetailWire | null> => {
      if (!code || !organizationId) return null;
      try {
        const result = await authedGet<PersonDetailResponse>(
          `${peopleBase(organizationId)}/people/by-code/${encodeURIComponent(code)}`,
        );
        return result.person;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!code && !!organizationId,
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

type PersonPayload = Record<string, unknown>;

const CREATE_KEYS: ReadonlySet<string> = new Set([...PERSON_SCALAR_KEYS, ...PERSON_CHILD_KEYS]);
const UPDATE_KEYS: ReadonlySet<string> = new Set([
  ...PERSON_SCALAR_KEYS,
  ...PERSON_CHILD_KEYS,
  ...PERSON_PHOTO_KEYS,
]);

/**
 * Keep only the keys the (strict) write schema accepts, dropping `undefined`.
 * Legacy callers still send `organization_id` (the route param now), `code`
 * (trigger-generated) and discipline `context_type`/`context_id` (not
 * writable through the payload); an unknown top-level key would be a 400.
 */
function toPersonPayload(data: object, allowed: ReadonlySet<string>): PersonPayload {
  const payload: PersonPayload = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || !allowed.has(key)) continue;
    if (key === 'disciplines' && Array.isArray(value)) {
      payload.disciplines = value.map((row: Record<string, unknown>) => {
        const { context_type: _contextType, context_id: _contextId, ...rest } = row;
        return rest;
      });
      continue;
    }
    payload[key] = value;
  }
  return payload;
}

/** The coded 409 envelope a person write can carry (`err.envelope`), or `null`. */
export function personErrorCodeOf(error: unknown): string | null {
  const code = (error as { envelope?: { code?: unknown } } | null | undefined)?.envelope?.code;
  return typeof code === 'string' ? code : null;
}

/** The `person_archive_only` refusal of a DELETE, with its `reasons`, or `null`. */
export function personArchiveOnlyOf(error: unknown): PersonArchiveOnlyErrorBody | null {
  if (personErrorCodeOf(error) !== PERSON_ERROR_CODES.archiveOnly) return null;
  return (error as { envelope: PersonArchiveOnlyErrorBody }).envelope;
}

/** The `connection_type_in_use` refusal (a DELETE / direction change on a used type), with its `count`, or `null`. */
export function connectionTypeInUseOf(error: unknown): ConnectionTypeInUseErrorBody | null {
  if (personErrorCodeOf(error) !== PERSON_ERROR_CODES.connectionTypeInUse) return null;
  return (error as { envelope: ConnectionTypeInUseErrorBody }).envelope;
}

/** The `connection_type_name_taken` refusal (a create / rename onto a taken name), or `null`. */
export function connectionTypeNameTakenOf(error: unknown): ConnectionTypeNameTakenErrorBody | null {
  if (personErrorCodeOf(error) !== PERSON_ERROR_CODES.connectionTypeNameTaken) return null;
  return (error as { envelope: ConnectionTypeNameTakenErrorBody }).envelope;
}

/**
 * The erased / archive-only 409s are user-facing rules, not bugs: App.tsx's
 * MutationCache reads this and keeps Sentry quiet for them (every other
 * mutation error is still captured there — the hooks never capture twice).
 */
export const PERSON_MUTATION_META = {
  expectedErrorCodes: [PERSON_ERROR_CODES.erased, PERSON_ERROR_CODES.archiveOnly] as readonly string[],
} as const;

function toastPersonError(error: Error, failedKey: string) {
  const code = personErrorCodeOf(error);
  if (code === PERSON_ERROR_CODES.erased) {
    toast.error(i18n.t('common:people.toast.erased'));
    return;
  }
  if (code === PERSON_ERROR_CODES.archiveOnly) {
    toast.error(i18n.t('common:people.toast.archiveOnly'));
    return;
  }
  toast.error(i18n.t(failedKey), error.message ? { description: error.message } : undefined);
}

/**
 * Create a person and its four child sets in ONE request (`create_person`,
 * atomic). Call-compatible with the old hook: `organization_id` rides in the
 * data (the context org is the fallback) and is stripped from the body.
 * Resolves to the created `PersonDetailWire`.
 */
export const useCreatePerson = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const contextOrgId = selectedOrganization?.id;

  return useMutation({
    meta: PERSON_MUTATION_META,
    mutationFn: async (
      data: CreatePersonData & { organization_id?: string },
    ): Promise<PersonDetailWire> => {
      const organizationId = data.organization_id ?? contextOrgId;
      if (!organizationId) throw new Error('No organization selected');
      const result = await authedPost<PersonDetailResponse>(
        `${peopleBase(organizationId)}/people`,
        toPersonPayload(data, CREATE_KEYS),
      );
      return result.person;
    },
    onSuccess: (person) => {
      void queryClient.invalidateQueries({ queryKey: personKeys.org(person.organization_id ?? contextOrgId) });
      void queryClient.invalidateQueries({ queryKey: ['businesses', person.organization_id ?? contextOrgId] });
      toast.success(i18n.t('common:people.toast.created', { name: person.full_name }));
    },
    onError: (error: Error) => toastPersonError(error, 'common:people.toast.createFailed'),
  });
};

/**
 * Update a person (PATCH, `update_person`). A present scalar key is written;
 * a present child array REPLACES that set; absent keys are left alone.
 * Call-compatible with the old hook (`{ id, ...fields }`); the org is the
 * selected organization. Archive is `mutate({ id, status: 'inactive' })`.
 */
export const useUpdatePerson = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: PERSON_MUTATION_META,
    mutationFn: async (data: UpdatePersonData): Promise<PersonDetailWire> => {
      if (!organizationId) throw new Error('No organization selected');
      const { id, ...fields } = data;
      const result = await authedPatch<PersonDetailResponse>(
        `${peopleBase(organizationId)}/people/${id}`,
        toPersonPayload(fields, UPDATE_KEYS),
      );
      return result.person;
    },
    onSuccess: (person) => {
      void queryClient.invalidateQueries({ queryKey: personKeys.org(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ['businesses', organizationId] });
      toast.success(i18n.t('common:people.toast.updated', { name: person.full_name }));
    },
    onError: (error: Error) => toastPersonError(error, 'common:people.toast.updateFailed'),
  });
};

/**
 * Delete a person. The route answers 409 `person_archive_only` (with
 * `reasons`) when the person is referenced by an AP bill, an expense, an AR
 * invoice, a ledger transaction, a time entry / timesheet, a sole-subject
 * consent record, or was GDPR-erased — read it with {@link personArchiveOnlyOf}
 * and archive instead. The org is the selected organization.
 */
export const useDeletePerson = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    meta: PERSON_MUTATION_META,
    mutationFn: async (personId: string): Promise<string> => {
      if (!organizationId) throw new Error('No organization selected');
      await authedDelete<PersonDeleteResponse>(`${peopleBase(organizationId)}/people/${personId}`);
      return personId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: personKeys.org(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ['businesses', organizationId] });
      toast.success(i18n.t('common:people.toast.deleted'));
    },
    onError: (error: Error) => toastPersonError(error, 'common:people.toast.deleteFailed'),
  });
};
